import React, { forwardRef, useState } from 'react';

import { Select } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { AlineInput } from 'src/AlineInput';
import { Spacer, useHandleAddCollaborator, WorkflowCollaboratorsChooser } from 'src/components';
import { getActionLastRemindedAt } from 'src/components/dashboard';
import { DragIcon } from 'src/components/icons/DragIcon';
import { GreenTickIcon } from 'src/components/icons/GreenTickIcon';
import { SmallSigningIcon } from 'src/components/icons/SmallSigningIcon';
import { UpcomingIcon } from 'src/components/icons/UpcomingIcon';
import { AssignRolesButton } from 'src/components/workflow/AssignRolesButton';
import { CollaboratorAvatarAndText } from 'src/components/workflow/workflow-builder/WorkflowCollaboratorsList';
import type { WorkflowAssignmentSelection } from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { Apollo } from 'src/gql/generated';
import type { ActionType } from 'src/hooks';
import { useGetAreWorkflowStepsEditable } from 'src/hooks';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import {
  getIsPlaceholderPermission,
  getIsRolePermission,
} from 'src/types/apollo/workflow-permission';
import type { WorkflowRecipientAction, WorkflowStep } from 'src/workflow';
import {
  ActionDropdown,
  DeleteActionButton,
  useActionRowStyles,
  workflowCollaboratorChooserWidthPx,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()({
  nameInput: {
    width: 160,
  },
});

export type EnvelopeRecipientActionWithStep = WorkflowRecipientAction & { step: WorkflowStep };
interface RecipientActionRowProps extends React.HTMLAttributes<HTMLElement> {
  recipientAction: EnvelopeRecipientActionWithStep;
  workflowId: UUID;
  workflowState: Apollo.WorkflowState;
}

export const RecipientActionRow = forwardRef(function RecipientActionRow(
  { className, recipientAction, workflowId, workflowState }: RecipientActionRowProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { classes } = useStyles();
  const { classes: actionRowClasses } = useActionRowStyles();
  const { getAreWorkflowStepsEditable } = useGetAreWorkflowStepsEditable();
  const isSigner = recipientAction.type === Apollo.WorkflowRecipientType.Signer;
  const areStepsEditable = getAreWorkflowStepsEditable({
    __typename: 'Workflow',
    state: workflowState,
  });
  const activeActionType = isSigner ? 'field' : 'cc';
  const [recipientName, setRecipientName] = useState(recipientAction.recipientName ?? '');
  const [updateRecipientName] = Apollo.useUpdateRecipientNameMutation();

  return (
    <div
      ref={ref}
      className={clsx(actionRowClasses.actionRow, className)}
      data-cy={
        isSigner
          ? Select.workflow.detail.steps.action.recipientActionRow
          : Select.workflow.detail.steps.action.carbonCopyRow
      }
    >
      {areStepsEditable && <DragIcon height={16} width={16} />}
      <WorkflowRecipientAvatar
        isWorkflowEditable={areStepsEditable}
        permission={recipientAction.permission}
        recipientId={recipientAction.id}
        workflowId={workflowId}
        onAutoFillName={name => setRecipientName(name)}
      />
      {areStepsEditable && !getIsPlaceholderPermission(recipientAction.permission) && (
        <AlineInput
          className={classes.nameInput}
          fullWidth={false}
          placeholder="Name"
          value={recipientName}
          // Save on blur rather than onChange to avoid a mutation on every keystroke.
          onBlur={async () => {
            await updateRecipientName({
              variables: {
                input: {
                  recipientId: recipientAction.id,
                  // Treat empty string the same as no name (|| not ?? so '' becomes null).
                  recipientName: recipientName || null,
                },
              },
            });
          }}
          onChange={e => setRecipientName(e.target.value)}
        />
      )}
      <WorkflowActionStateNotification
        action={recipientAction}
        isSigner={isSigner}
        isWorkflowEditable={areStepsEditable}
      />
      <Spacer />
      <WorkflowRecipientActionSelector
        action={recipientAction}
        activeActionType={activeActionType}
        isWorkflowEditable={areStepsEditable}
        recipientId={recipientAction.id}
        workflowId={workflowId}
      />
      <DeleteActionButton action={recipientAction} workflowId={workflowId} />
    </div>
  );
});

const useWorkflowRecipientAvatarStyles = makeStyles()(() => ({
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  wrapper: {
    width: workflowCollaboratorChooserWidthPx,
  },
}));

export function WorkflowRecipientAvatar({
  isWorkflowEditable,
  onAutoFillName,
  permission,
  recipientId,
  workflowId,
}: {
  isWorkflowEditable: boolean;
  permission: WorkflowPermissionSubset;
  recipientId: UUID;
  workflowId: UUID;
  onAutoFillName?: (name: string) => void;
}) {
  const { classes } = useWorkflowRecipientAvatarStyles();
  const { handleAddCollaborator } = useHandleAddCollaborator(workflowId);
  const [changeRecipientAssignment] = Apollo.useChangeRecipientAssignmentMutation();
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const [updateRecipientName] = Apollo.useUpdateRecipientNameMutation();
  const placeholderRole = getIsRolePermission(permission) ? permission.role : null;

  if (!isWorkflowEditable)
    return (
      <div className={classes.wrapper}>
        <CollaboratorAvatarAndText permission={permission} />
      </div>
    );

  // Returns the display name to auto-fill when the user picks an existing person.
  // Returns '' for roles and anonymous emails so the name field is left blank.
  function getAutoName(value: WorkflowAssignmentSelection): string {
    switch (value.__typename) {
      case 'WorkflowPermission':
        return value.account?.__typename === 'User' ? value.account.displayName : '';
      case 'User':
        return value.displayName;
      default:
        return '';
    }
  }

  return (
    <>
      <div className={classes.wrapper}>
        <WorkflowCollaboratorsChooser
          emailOnly
          value={permission}
          workflowId={workflowId}
          onChange={async (_, value) => {
            if (!value) return;

            const autoName = getAutoName(value);
            // Always call so that picking a role clears the parent's name state.
            onAutoFillName?.(autoName);

            /**
             * A person picked for a role placeholder fills the role, so every step and task that
             * names it gets them. Another role or an existing collaborator swaps the recipient.
             */
            const personFill = placeholderRole ? getPersonFillInput(value) : null;
            if (personFill) {
              await changeCollaboratorAssignment({
                variables: { input: { ...personFill, permissionId: permission.id } },
              });
              return;
            }

            if (value.__typename === 'WorkflowPermission') {
              await changeRecipientAssignment({
                variables: {
                  input: {
                    permissionId: value.id,
                    recipientId,
                  },
                },
              });
              if (getIsPlaceholderPermission(value))
                await updateRecipientName({
                  variables: { input: { recipientId, recipientName: null } },
                });

              return;
            }

            const collaborator = await handleAddCollaborator(value);
            if (collaborator) {
              await changeRecipientAssignment({
                variables: {
                  input: {
                    permissionId: collaborator.id,
                    recipientId,
                  },
                },
              });
              if (getIsRolePermission(collaborator))
                await updateRecipientName({
                  variables: { input: { recipientId, recipientName: null } },
                });
            }
          }}
        />
      </div>
      {placeholderRole && (
        <AssignRolesButton
          dialogTitle={`Assign ${placeholderRole.roleName}`}
          label={`Assign ${placeholderRole.roleName}`}
          roleIds={[placeholderRole.id]}
          workflowId={workflowId}
        />
      )}
    </>
  );
}

function getPersonFillInput(
  value: WorkflowAssignmentSelection
): { assignedUserId: UUID } | { email: string } | null {
  switch (value.__typename) {
    case 'User':
      return { assignedUserId: value.id };
    case 'NewCollaborator':
      return value.type === 'email' ? { email: value.text } : null;
    case 'AlineAi':
    case 'UserDefinedRole':
    case 'WorkflowPermission':
      return null;
  }
}

export function WorkflowRecipientActionSelector({
  action,
  activeActionType,
  isWorkflowEditable,
  recipientId,
  workflowId,
}: {
  action: EnvelopeRecipientActionWithStep;
  activeActionType: ActionType | null;
  isWorkflowEditable: boolean;
  recipientId: UUID;
  workflowId: UUID;
}) {
  const [chosenAction, setChosenAction] = useState<ActionType | null>(activeActionType);
  const [changeRecipientAction, { loading }] = Apollo.useChangeRecipientActionMutation();

  const onSetChosenAction = async (actionType: ActionType) => {
    setChosenAction(actionType);
    await changeRecipientAction({
      variables: {
        input: {
          action:
            actionType === 'field'
              ? Apollo.WorkflowRecipientType.Signer
              : Apollo.WorkflowRecipientType.CarbonCopy,
          actionId: action.id,
          recipientId,
        },
      },
    });
  };

  if (!isWorkflowEditable) return null;

  return (
    <ActionDropdown
      assignee={{
        assignedRoleId: action.permission?.role?.id ?? null,
        assignedUserId:
          action.permission.account?.__typename === 'User' ? action.permission.account.id : null,
        isLauncher: action.permission.isLauncher,
      }}
      chosenAction={chosenAction}
      disabled={loading}
      workflowId={workflowId}
      onSetChosenAction={onSetChosenAction}
    />
  );
}

const useWorkflowActionStateNotificationStyles = makeStyles()(theme => ({
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    marginLeft: theme.spacing(3),
  },
}));

export function WorkflowActionStateNotification({
  action,
  isSigner,
  isWorkflowEditable,
}: {
  action: EnvelopeRecipientActionWithStep;
  isSigner: boolean;
  isWorkflowEditable: boolean;
}) {
  const { classes } = useWorkflowActionStateNotificationStyles();

  if (isWorkflowEditable) return null;

  switch (action.status) {
    case Apollo.WorkflowRecipientStatus.Completed: {
      const completedAtString = action.completedAt
        ? format(parseISO(action.completedAt), 'MMM d, yyyy')
        : '';
      const signerCopy = completedAtString
        ? `Signed on ${completedAtString}`
        : 'Signature complete';
      const carbonCopy = completedAtString ? `Copy sent on ${completedAtString}` : 'Copy sent';
      const copy = isSigner ? signerCopy : carbonCopy;

      return (
        <div className={classes.wrapper}>
          <GreenTickIcon />
          <Typography>{copy}</Typography>
        </div>
      );
    }
    case Apollo.WorkflowRecipientStatus.Sent: {
      return (
        <div className={classes.wrapper}>
          <SmallSigningIcon />
          <Typography>{getSentStatusMessage(action)}</Typography>
        </div>
      );
    }
    case Apollo.WorkflowRecipientStatus.Unsent: {
      const copy = isSigner ? 'Upcoming signature' : 'Upcoming CC';

      return (
        <div className={classes.wrapper}>
          <UpcomingIcon stroke="#323232" />
          <Typography>{copy}</Typography>
        </div>
      );
    }
    default:
      return null;
  }
}

function getSentStatusMessage(action: EnvelopeRecipientActionWithStep): string {
  const remindedAt = getActionLastRemindedAt(action);
  const dateFormat = 'MMM d, yyyy';

  let baseMessage = 'Recently sent';
  if (remindedAt) {
    baseMessage = `Last reminded on ${format(parseISO(remindedAt), dateFormat)}`;
  } else if (action.startedAt) {
    baseMessage = `Sent on ${format(parseISO(action.startedAt), dateFormat)}`;
  }

  if (!action.lastViewedAt) return baseMessage;

  return `${baseMessage}, last viewed ${format(parseISO(action.lastViewedAt), dateFormat)}`;
}
